/**
 * Contact-type taxonomy — single source of truth for the frontend.
 *
 * Before 2026-05 the list was duplicated inline in five places
 * (NewCustomerModal, CustomerListing, ClientsFilters, ClientsList,
 * ClientDetailPanel) — adding a new value meant remembering to edit
 * five files plus the backend validator. Now everything imports from
 * here.
 *
 * Adding a new contact type:
 *   1. Add the literal to CONTACT_TYPES below
 *   2. Add a badge variant to CONTACT_TYPE_BADGE_CLASS
 *   3. Add a sort weight to CONTACT_TYPE_SORT_ORDER
 *   4. Add it to VALID_CONTACT_TYPE in functions/src/functions/http/agent.ts
 *      (server-side validator for the agent's createCustomer tool)
 *
 * Anywhere else in the app that touches contact-type just imports
 * from this file.
 */

export const CONTACT_TYPES = [
  'Lead',
  'Customer',
  'Former Customer',
  'Failed Lead',
  'Supplier',
  'Personal',
] as const;

export type ContactType = typeof CONTACT_TYPES[number];

/** Filter-dropdown value type — same as ContactType plus the 'all'
 *  sentinel that means "no filter". Used by ClientsFilters etc. */
export type ContactTypeFilter = 'all' | ContactType;

/** Bootstrap/Metronic badge class per contact type. Kept here so the
 *  duplicate getContactTypeBadge functions across components can share
 *  one definition. */
export const CONTACT_TYPE_BADGE_CLASS: Record<ContactType, string> = {
  'Lead':            'badge-light-info',
  'Customer':        'badge-light-success',
  'Former Customer': 'badge-light-warning',
  'Failed Lead':     'badge-light-danger',
  'Supplier':        'badge-light-primary',
  'Personal':        'badge-light-secondary',
};

/** Sort order used when grouping by contact type. Lower = earlier in
 *  the list. The order mirrors how a user typically thinks about the
 *  funnel: prospects → current → past → never-converted → other. */
export const CONTACT_TYPE_SORT_ORDER: Record<ContactType, number> = {
  'Lead':            0,
  'Customer':        1,
  'Former Customer': 2,
  'Failed Lead':     3,
  'Supplier':        4,
  'Personal':        5,
};

/** Plural form for filter labels ("Leads", "Customers", "Former Customers"). */
export const CONTACT_TYPE_LABEL_PLURAL: Record<ContactType, string> = {
  'Lead':            'Leads',
  'Customer':        'Customers',
  'Former Customer': 'Former Customers',
  'Failed Lead':     'Failed Leads',
  'Supplier':        'Suppliers',
  'Personal':        'Personal',
};
